import { Stack } from '@mui/material';
import type { BrandWithSupport } from 'src/graphql';
import { LandingPagePreview } from 'src/pages/Settings/memberships/components/landing-page-preview';
import { LegalDocumentPage } from 'src/pages/Settings/memberships/components/legal-document/legal-document-page';
import { LiveLink } from 'src/pages/Settings/memberships/components/live-link';
import { MembershipsConfigPreview } from 'src/pages/Settings/memberships/components/memberships-config-preview';
import { useLandingPageLiveLink } from 'src/pages/Settings/memberships/hooks/use-landing-page-live-link';
import { useLandingPagePreview } from 'src/pages/Settings/memberships/hooks/use-landing-page-preview';
import type { useLandingPageSurface } from 'src/pages/Settings/memberships/hooks/use-landing-page-surface';
import { useLegalDocumentLiveLink } from 'src/pages/Settings/memberships/hooks/use-legal-document-live-link';
import type { LegalDocumentSurface } from 'src/pages/Settings/memberships/hooks/use-legal-document-surface';
import type { useMembershipsConfigPreview } from 'src/pages/Settings/memberships/hooks/use-memberships-config-preview';
import { LandingPagePreviewContainer } from 'src/pages/Settings/memberships/styled';
import { toLandingPagePartner } from 'src/pages/Settings/memberships/utils/to-landing-page-partner';
import { useMembershipsConfigStore } from 'src/store/memberships-config';

type ConfigPreviewPaneProps = {
  isLandingPageSurface: boolean;
  preview: ReturnType<typeof useMembershipsConfigPreview>;
  landingPageSurface: ReturnType<typeof useLandingPageSurface>;
  legalDocumentSurface: LegalDocumentSurface | null;
  segment?: BrandWithSupport;
  termsOfServiceUrl?: string;
};

export const ConfigPreviewPane = ({
  isLandingPageSurface,
  preview,
  landingPageSurface,
  legalDocumentSurface,
  segment,
  termsOfServiceUrl,
}: ConfigPreviewPaneProps) => {
  const previewViewport = useMembershipsConfigStore.use.previewViewport();
  const setPreviewViewport = useMembershipsConfigStore.use.setPreviewViewport();
  const landingPagePreview = useLandingPagePreview({
    data: landingPageSurface.data,
    liveFormValues: landingPageSurface.liveFormValues,
    storedConfig: landingPageSurface.storedConfig,
    plansConfig: preview.previewConfig,
  });
  const brandId = segment?.brandId ? String(segment.brandId) : undefined;
  // The saved slug, not the form's: the address only changes once the page is saved.
  const liveLink = useLandingPageLiveLink({
    partnerId: isLandingPageSurface ? (segment?.partnerId ?? null) : null,
    brandId,
    slug: landingPageSurface.data.slug,
    configLookup: landingPageSurface.configLookup,
  });
  const legalDocumentLiveLink = useLegalDocumentLiveLink(
    segment?.partnerId ?? null,
    brandId,
    legalDocumentSurface,
  );

  if (legalDocumentSurface) {
    return (
      <LandingPagePreviewContainer>
        <Stack direction="row" sx={{ width: '100%' }}>
          <LiveLink state={legalDocumentLiveLink} />
        </Stack>
        {/* Keyed by kind: its Controllers stay bound to the form they mounted with. */}
        <LegalDocumentPage key={legalDocumentSurface.kind} surface={legalDocumentSurface} />
      </LandingPagePreviewContainer>
    );
  }

  if (isLandingPageSurface) {
    return (
      <LandingPagePreview
        config={landingPagePreview.config}
        plans={landingPagePreview.plans}
        viewport={previewViewport}
        onViewportChange={setPreviewViewport}
        partner={toLandingPagePartner(segment, termsOfServiceUrl)}
        logoUrl={segment?.logo ?? undefined}
        brandId={brandId}
        liveLink={liveLink}
      />
    );
  }

  return (
    <MembershipsConfigPreview
      config={preview.previewConfig}
      branding={preview.previewBranding}
      backgroundImageUrl={preview.previewBackgroundImageUrl}
    />
  );
};
